import { motion } from "framer-motion";
import InteractiveWidget from "@/components/LightDiagrams";
import { Info, AlertTriangle, Lightbulb, Sigma, ListChecks } from "lucide-react";

const calloutCfg = {
  info: { icon: Info, color: "var(--cool)", bg: "rgba(96,165,250,0.08)", border: "rgba(96,165,250,0.3)" },
  warning: { icon: AlertTriangle, color: "#F5A623", bg: "rgba(245,166,35,0.08)", border: "rgba(245,166,35,0.3)" },
  tip: { icon: Lightbulb, color: "#D4AF37", bg: "rgba(212,175,55,0.08)", border: "rgba(212,175,55,0.3)" },
};

// Renders a formula string with LaTeX-lite subscripts: _word or _{word} -> <sub>
function FormulaExpr({ expr }) {
  const parts = [];
  const regex = /_\{([^}]+)\}|_([A-Za-z0-9äöüÄÖÜ]+)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(expr)) !== null) {
    if (m.index > last) parts.push(expr.slice(last, m.index));
    parts.push(
      <sub key={key++} className="text-[0.62em] font-normal">
        {m[1] ?? m[2]}
      </sub>
    );
    last = regex.lastIndex;
  }
  if (last < expr.length) parts.push(expr.slice(last));
  return <>{parts}</>;
}

function CaseStudyCard({ block }) {
  return (
    <div className="my-6 rounded-2xl glass overflow-hidden border border-white/10" data-testid="casestudy-card">
      <div className="relative h-40 sm:h-48">
        <img src={block.image} alt={block.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, var(--tw-gradient-from,#11151c), rgba(17,21,28,0.45) 55%, transparent)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
        <div className="absolute bottom-3 left-4 right-4">
          <span className="text-[10px] uppercase tracking-[0.2em] text-gold px-2 py-0.5 rounded bg-black/55 border border-gold/30">Fallstudie</span>
          <h4 className="font-heading font-bold text-xl text-white mt-1.5">{block.title}</h4>
        </div>
      </div>
      <div className="p-5">
        <p className="text-sm text-foreground/80 leading-relaxed">{block.summary}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          {block.targets.map((t, i) => (
            <div key={i} className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t.label}</div>
              <div className="font-mono text-sm text-gold mt-0.5">{t.value}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-start gap-2.5">
          <ListChecks className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
          <ul className="space-y-1.5">
            {block.strategy.map((s, i) => (
              <li key={i} className="text-sm text-foreground/80 leading-relaxed">{s}</li>
            ))}
          </ul>
        </div>
        <div className="mt-4 rounded-lg p-3 flex gap-3" style={{ background: "rgba(245,166,35,0.08)", border: "1px solid rgba(245,166,35,0.3)" }}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: "#F5A623" }} />
          <div>
            <span className="font-heading font-bold text-xs" style={{ color: "#F5A623" }}>Typischer Fehler</span>
            <p className="text-sm text-foreground/80 mt-0.5 leading-relaxed">{block.pitfall}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "heading":
      return <h3 className="font-heading font-bold text-xl sm:text-2xl mt-8 mb-1 text-foreground">{block.text}</h3>;
    case "paragraph":
      return <p className="text-base leading-relaxed text-foreground/85 my-3">{block.text}</p>;
    case "list":
      return block.ordered ? (
        <ol className="list-none space-y-2.5 my-4">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3 text-foreground/85">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-gold/15 border border-gold/30 text-gold text-xs font-mono flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <span className="leading-relaxed">{it}</span>
            </li>
          ))}
        </ol>
      ) : (
        <ul className="space-y-2.5 my-4">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3 text-foreground/85">
              <span className="flex-shrink-0 h-1.5 w-1.5 rounded-full bg-gold mt-2.5" />
              <span className="leading-relaxed">{it}</span>
            </li>
          ))}
        </ul>
      );
    case "callout": {
      const c = calloutCfg[block.variant] || calloutCfg.info;
      const Icon = c.icon;
      return (
        <div className="my-5 rounded-xl p-4 sm:p-5 flex gap-4" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
          <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: c.color }} />
          <div>
            <div className="font-heading font-bold text-sm" style={{ color: c.color }}>{block.title}</div>
            <p className="text-sm text-foreground/80 mt-1 leading-relaxed">{block.text}</p>
          </div>
        </div>
      );
    }
    case "formula":
      return (
        <div className="my-5 rounded-xl glass p-5 flex items-start gap-4">
          <Sigma className="h-5 w-5 text-gold mt-1 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{block.label}</div>
            <div className="font-mono text-xl sm:text-2xl text-gold my-1.5"><FormulaExpr expr={block.expr} /></div>
            <p className="text-sm text-foreground/70 leading-relaxed">{block.desc}</p>
          </div>
        </div>
      );
    case "image":
      return (
        <figure className="my-6">
          <div className="rounded-xl overflow-hidden border border-white/10">
            <img src={block.url} alt={block.caption} className="w-full object-cover" loading="lazy" />
          </div>
          {block.caption && <figcaption className="text-xs text-muted-foreground mt-2 text-center italic">{block.caption}</figcaption>}
        </figure>
      );
    case "table":
      return (
        <div className="my-6">
          <div className="rounded-xl glass overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b hairline">
                  {block.headers.map((h, i) => (
                    <th key={i} className="text-left font-heading font-bold text-gold px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, ri) => (
                  <tr key={ri} className="border-b hairline last:border-0 hover:bg-white/[0.02]">
                    {row.map((cell, ci) => (
                      <td key={ci} className={`px-4 py-3 ${ci === 0 ? "font-medium text-foreground" : "text-foreground/75"} ${/\d/.test(cell) && ci > 0 ? "font-mono" : ""}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.caption && <p className="text-xs text-muted-foreground mt-2 italic">{block.caption}</p>}
        </div>
      );
    case "keyterms":
      return (
        <div className="my-5 grid sm:grid-cols-2 gap-3">
          {block.terms.map((t, i) => (
            <div key={i} className="rounded-xl glass p-4">
              <div className="font-heading font-bold text-sm text-foreground">{t.term}</div>
              <p className="text-sm text-foreground/70 mt-1 leading-relaxed">{t.def}</p>
            </div>
          ))}
        </div>
      );
    case "interactive":
      return (
        <div className="my-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.2em] text-gold px-2 py-0.5 rounded bg-gold/10 border border-gold/20">Interaktiv</span>
            <span className="font-heading font-bold text-sm text-foreground">{block.title}</span>
          </div>
          {block.caption && <p className="text-sm text-muted-foreground mb-2">{block.caption}</p>}
          <InteractiveWidget widget={block.widget} />
        </div>
      );
    case "casestudy":
      return <CaseStudyCard block={block} />;
    default:
      return null;
  }
}

export default function LessonBlocks({ blocks }) {
  return (
    <div>
      {blocks.map((b, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.45, delay: Math.min(i * 0.03, 0.2) }}
        >
          <Block block={b} />
        </motion.div>
      ))}
    </div>
  );
}
